import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SwitchInput from '@/components/Inputs/Switch';
import Tooltip from '@/components/Tooltip';
import { BORDER_RADIUS } from '@/constants/config';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';

/**
 * MemoryPreferencesCard
 *
 * Polished, cohesive controls card for:
 * 1. Referencing memory in conversations
 * 2. Background learning / dream synthesis from chats
 * Includes Plus perks & upsell link.
 */
function MemoryPreferencesCard({
  isMemoryEnabled,
  isSaving = false,
  onToggleMemoryEnabled,
  isLearningEnabled,
  onToggleLearning,
  isPlus,
  onPlusUpsell,
}) {
  const { colors, isDark } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const memColors = useMemo(() => getMemoryThemeColors(isDark, colors), [isDark, colors]);

  const styles = useMemo(
    () => createStyles(colors, memColors, isDark, isXS, isSM),
    [colors, memColors, isDark, isXS, isSM]
  );

  return (
    <View style={styles.card}>
      {/* Header bar */}
      <Pressable
        style={styles.header}
        onPress={() => setIsExpanded(prev => !prev)}
        accessibilityRole="button"
        accessibilityLabel={t('pages.memory.preferencesTitle')}
        accessibilityState={{ expanded: isExpanded }}
        {...(isWeb ? { 'aria-expanded': isExpanded } : {})}
      >
        <View style={styles.headerLeft}>
          <View style={styles.iconCircle}>
            <Icon name="gear" size={14} color={colors.text_color_primary} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text size="s" weight="bold" color="primary">
              {t('pages.memory.preferencesTitle')}
            </Text>
            <Text size="xs" color="secondary">
              {t('pages.memory.preferencesDesc')}
            </Text>
          </View>
        </View>

        <Icon
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={13}
          color={colors.text_color_primary}
        />
      </Pressable>

      {/* Expanded controls content */}
      {isExpanded && (
        <View style={styles.content}>
          <View style={styles.togglesGrid}>
            {/* Control 1: Use Memories in Conversations */}
            <View style={styles.controlBox}>
              <View style={styles.controlInfo}>
                <View style={styles.controlTitleRow}>
                  <Text size="n" weight="semibold" color="primary">
                    {t('pages.memory.useInConversations')}
                  </Text>
                  <Tooltip label={t('pages.memory.useInConversationsDesc')} side="top" showIsXs>
                    <Icon name="circle-info" size={13} color={colors.text_color_secondary} />
                  </Tooltip>
                </View>
                <Text size="xs" color="secondary" style={styles.controlDesc}>
                  {t('pages.memory.useInConversationsDesc')}
                </Text>
              </View>

              <View style={styles.switchCol}>
                <SwitchInput
                  disabled={isSaving}
                  accessibilityLabel={t('pages.memory.useInConversations')}
                  value={isMemoryEnabled}
                  onValueChange={onToggleMemoryEnabled}
                />
              </View>
            </View>

            {/* Control 2: Background Learning */}
            <View style={styles.controlBox}>
              <View style={styles.controlInfo}>
                <View style={styles.controlTitleRow}>
                  <Text size="n" weight="semibold" color="primary">
                    {t('pages.memory.learnFromConversations')}
                  </Text>
                  {isPlus ? (
                    <View style={styles.plusBadge}>
                      <Text size="xxs" weight="bold" style={styles.plusBadgeText}>
                        PLUS
                      </Text>
                    </View>
                  ) : null}
                  <Tooltip
                    label={
                      isPlus
                        ? t('pages.memory.learnFromConversationsDesc')
                        : t('pages.memory.learnFromConversationsDescFree')
                    }
                    side="top"
                    showIsXs
                  >
                    <Icon name="circle-info" size={13} color={colors.text_color_secondary} />
                  </Tooltip>
                </View>

                <Text size="xs" color="secondary" style={styles.controlDesc}>
                  {isPlus
                    ? t('pages.memory.learnFromConversationsDesc')
                    : t('pages.memory.learnFromConversationsDescFree')}
                </Text>

                {!isPlus && (
                  <Pressable onPress={onPlusUpsell} style={styles.upsellPressable}>
                    <Icon name="sparkles" size={12} color={memColors.accent} />
                    <Text size="xs" weight="semibold" style={{ color: memColors.accent }}>
                      {t('pages.memory.learnPlusUpsell')}
                    </Text>
                  </Pressable>
                )}
              </View>

              <View style={styles.switchCol}>
                <SwitchInput
                  disabled={isSaving}
                  accessibilityLabel={t('pages.memory.learnFromConversations')}
                  value={isLearningEnabled}
                  onValueChange={onToggleLearning}
                />
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors, memColors, isDark, isXS, isSM) =>
  StyleSheet.create({
    card: {
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      marginBottom: 18,
      overflow: 'hidden',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
      paddingHorizontal: 16,
      backgroundColor: colors.solid_color_secondary,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      flex: 1,
      minWidth: 0,
    },
    iconCircle: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      padding: isXS ? 14 : 16,
    },
    togglesGrid: {
      flexDirection: isSM || isXS ? 'column' : 'row',
      gap: 14,
    },
    controlBox: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      padding: 12,
      borderRadius: BORDER_RADIUS.medium,
      backgroundColor: colors.solid_color_secondary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    controlInfo: {
      flex: 1,
      minWidth: 0,
      gap: 4,
    },
    controlTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
    controlDesc: {
      lineHeight: 18,
    },
    switchCol: {
      flexShrink: 0,
      paddingTop: 2,
    },
    plusBadge: {
      backgroundColor: colors.solid_color_tertiary,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 6,
    },
    plusBadgeText: {
      color: colors.text_color_secondary,
      fontSize: 10,
    },
    upsellPressable: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      marginTop: 4,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
  });

export default React.memo(MemoryPreferencesCard);
